import React, { useState, useEffect } from "react";

export interface FormField<T> {
  key: keyof T;
  label: string;
  type: "text" | "email" | "number" | "textarea" | "select" | "date" | "file" | "checkbox";
  required?: boolean;
  defaultValue?: any;
  placeholder?: string;
  options?: Array<{ value: string | number; label: string }> | ((data: Partial<T>) => Array<{ value: string | number; label: string }>); // For select fields
  validation?: (value: any) => string | null; // Custom validation
}

export interface FormModalProps<T> {
  isOpen: boolean;
  title: string;
  data?: T | null; // null for create, T for edit
  fields: FormField<T>[];
  onSubmit: (data: T) => Promise<void> | void;
  onCancel: () => void;
  transformValues?: (data: Partial<T>, key: keyof T) => Partial<T>;
  loading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  checkboxGroupLabel?: string;
  checkboxGroupHint?: string;
}

export function FormModal<T extends Record<string, any>>({
  isOpen,
  title,
  data,
  fields,
  onSubmit,
  onCancel,
  transformValues,
  loading = false,
  submitLabel = "Salva",
  cancelLabel = "Annulla",
  checkboxGroupLabel = "Abilitazioni",
  checkboxGroupHint,
}: FormModalProps<T>) {
  const [formData, setFormData] = useState<Partial<T>>({});
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      if (data) {
        // Edit mode - populate with existing data
        setFormData({ ...data });
      } else {
        // Create mode - initialize empty form
        const emptyForm = fields.reduce((acc, field) => {
          acc[field.key] = (field.defaultValue ?? (field.type === "checkbox" ? false : "")) as T[keyof T];
          return acc;
        }, {} as Partial<T>);
        setFormData(emptyForm);
      }
      setErrors({});
      setSubmitError("");
    }
  // Fields are configured by the page; opening or changing the record resets the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  const handleInputChange = (key: keyof T, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [key]: value };
      return transformValues ? transformValues(next, key) : next;
    });
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};

    fields.forEach((field) => {
      const value = formData[field.key];

      // Required field validation
      if (field.required && (!value || String(value).trim() === "")) {
        newErrors[field.key] = `${field.label} è obbligatorio`;
        return;
      }

      // Email validation
      if (field.type === "email" && value && typeof value === "string") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field.key] = "Email non valida";
          return;
        }
      }

      // Custom validation
      if (field.validation && value) {
        const customError = field.validation(value);
        if (customError) {
          newErrors[field.key] = customError;
          return;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await onSubmit(formData as T);
      onCancel(); // Close modal on success
    } catch (error: any) {
      setSubmitError(error.message || "Impossibile salvare. Controlla i dati.");
      console.error("Form submission error:", error);
      // You could set a general error state here if needed
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField<T>) => {
    let value = formData[field.key] || "";
    // Se il campo è di tipo date e il valore è valorizzato, formatta in yyyy-mm-dd
    if (field.type === "date" && value) {
      // Se già nel formato giusto, non fare nulla
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        // ok
      } else {
        // Prova a convertire da Date o stringa compatibile
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          value = `${yyyy}-${mm}-${dd}`;
        }
      }
    }
    const error = errors[field.key];

    const baseClasses = `block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-500 focus:border-brand-500 ${
      error ? "border-red-300" : "border-gray-300"
    }`;

    switch (field.type) {
      case "checkbox":
        return <input id={String(field.key)} type="checkbox" checked={Boolean(formData[field.key])}
          onChange={e => handleInputChange(field.key, e.target.checked)} className="h-5 w-5 rounded border-gray-300 focus:ring-brand-500" />;
      case "textarea":
        return (
          <textarea
            id={String(field.key)}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className={baseClasses}
          />
        );

      case "select":
        return (
          <select
            id={String(field.key)}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            className={baseClasses}
          >
            <option value="">Seleziona...</option>
            {(typeof field.options === "function" ? field.options(formData) : field.options)?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "number":
        return (
          <input
            type="number"
            id={String(field.key)}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );

      case "date":
        return (
          <input
            type="date"
            id={String(field.key)}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseClasses}
            pattern="\d{4}-\d{2}-\d{2}"
            min="1900-01-01"
            max="2100-12-31"
          />
        );

      case "file":
        return (
          <input
            type="file"
            id={String(field.key)}
            onChange={(e) => {
              const file =
                e.target.files && e.target.files.length > 0
                  ? e.target.files[0]
                  : undefined;
              handleInputChange(field.key, file);
            }}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );

      default: // text, email
        return (
          <input
            type={field.type}
            id={String(field.key)}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onCancel}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {title}
                </h3>
                <button
                  type="button"
                  onClick={onCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {submitError && <p role="alert" className="mb-4 text-red-700">{submitError}</p>}
              {/* Form fields */}
              <div className="space-y-4">
                {fields.filter(field => field.type !== "checkbox").map((field) => (
                  <div key={String(field.key)}>
                    <label
                      htmlFor={String(field.key)}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {field.label}
                      {field.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    {renderField(field)}
                    {errors[field.key] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors[field.key]}
                      </p>
                    )}
                  </div>
                ))}
                {fields.some(field => field.type === "checkbox") && (
                  <fieldset className="border-t border-gray-200 pt-5">
                    <legend className="float-left w-full text-sm font-semibold text-gray-900 mb-1">{checkboxGroupLabel}</legend>
                    {checkboxGroupHint && <p className="clear-both mb-3 text-sm text-gray-500">{checkboxGroupHint}</p>}
                    <div className="clear-both grid gap-3 sm:grid-cols-2">
                      {fields.filter(field => field.type === "checkbox").map(field => (
                        <div key={String(field.key)}>
                          <label className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-4 transition-colors ${formData[field.key] ? "border-brand-300 bg-brand-50" : "border-gray-200 bg-white"}`}>
                            <span>
                              <span className="block text-sm font-semibold text-gray-900">{field.label}</span>
                              <span className="block mt-1 text-xs text-gray-500">{formData[field.key] ? "Abilitato" : "Non abilitato"}</span>
                            </span>
                            <span className="relative inline-flex shrink-0">
                              <input id={String(field.key)} type="checkbox" role="switch" aria-label={field.label} checked={Boolean(formData[field.key])}
                                disabled={isSubmitting || loading} onChange={e => handleInputChange(field.key, e.target.checked)}
                                className="peer sr-only" />
                              <span aria-hidden="true" className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-brand-600 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 peer-disabled:opacity-50" />
                              <span aria-hidden="true" className="pointer-events-none absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                            </span>
                          </label>
                          {errors[field.key] && <p className="mt-1 text-sm text-red-600">{errors[field.key]}</p>}
                        </div>
                      ))}
                    </div>
                  </fieldset>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="w-full inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-brand-600 text-base font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting || loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Salvando...
                  </>
                ) : (
                  submitLabel
                )}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting || loading}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
